/**
 * CR-7732: is the person viewing this a haulier?
 *
 * Two kinds of company carry loads, and neither can be recognised by company
 * type alone:
 *
 * * a `logistics` company — a haulier with no other role on the platform;
 * * a `supplier` a customer marked as its Logistics Provider — usually a
 *   haulier too, kept as a supplier because the ERP knows it by a supplier
 *   code and sends its haulage orders down that path.
 *
 * Both are here to move other people's goods, so neither has parts of its own
 * to plan, forecast or hold stock of. The surfaces that exist for that are
 * hidden from both, which is what this answers.
 */
import { useAuth } from '../contexts/AuthContext';
import { useMenuSettings } from '../contexts/MenuSettingsContext';

/**
 * Whether the signed-in user's company carries loads for a customer.
 *
 * @returns true for a logistics company, and for a supplier with the
 *   Logistics Provider flag (`logistics_view`) switched on
 *
 * @example
 * const isCarrier = useIsCarrier();
 * if (isCarrier) return null; // no stock surfaces for a haulier
 */
export function useIsCarrier(): boolean {
  const { user } = useAuth();
  const { isFeatureEnabled } = useMenuSettings();
  if (user?.company_type === 'logistics') return true;
  // The flag is granted to suppliers, so the supplier side is required as
  // well as the flag: a customer that somehow carried it would otherwise lose
  // its own parts and planning screens.
  const isSupplier = user?.company_type === 'supplier' || user?.platform === 'SUPPLIER';
  return isSupplier && isFeatureEnabled('logistics_view');
}
